import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, Mail, Clock } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { CONTACT } from "../data/translations";
import Logo from "./Logo";

const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/references", label: t.nav.references },
    { to: "/contact", label: t.nav.contact },
  ];

  const linkCls = ({ isActive }) =>
    `text-sm font-medium tracking-wide transition-colors duration-200 ${
      isActive ? "text-brand" : "text-slate-300 hover:text-white"
    }`;

  const idFor = (to) => (to === "/" ? "home" : to.slice(1));

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="navbar">
      <div className="hidden md:block bg-black border-b border-white/10">
        <div className="container-site flex items-center justify-between py-2 font-mono text-[11px] tracking-wider text-slate-400">
          <div className="flex items-center gap-6">
            <a href={CONTACT.phoneHref} data-testid="topbar-phone" className="flex items-center gap-2 hover:text-brand transition-colors">
              <Phone size={12} className="text-brand" /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="topbar-email" className="flex items-center gap-2 hover:text-brand transition-colors">
              <Mail size={12} className="text-brand" /> {CONTACT.email}
            </a>
          </div>
          <span className="flex items-center gap-2">
            <Clock size={12} className="text-brand" /> {t.topbar.hours}
          </span>
        </div>
      </div>

      <div
        className={`transition-all duration-300 ${
          scrolled || open ? "bg-black/95 backdrop-blur-md border-b border-white/10" : "bg-gradient-to-b from-black/70 to-transparent border-b border-transparent"
        }`}
      >
        <div className="container-site flex items-center justify-between h-16 md:h-[72px]">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.to === "/"} className={linkCls} data-testid={`nav-link-${idFor(l.to)}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <div className="flex border border-white/15" data-testid="language-toggle">
              {["en", "tr"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-toggle-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-3 py-1.5 font-mono text-xs font-bold tracking-widest transition-colors duration-200 ${
                    lang === l ? "bg-brand text-black" : "text-slate-300 hover:text-white"
                  }`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <Link to="/contact" data-testid="nav-quote-button" className="hidden lg:inline-flex btn-primary !px-5 !py-2.5">
              {t.nav.quote}
            </Link>
            <button className="lg:hidden text-white p-1" onClick={() => setOpen(!open)} data-testid="mobile-menu-button" aria-label="Menu">
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-black border-t border-white/10" data-testid="mobile-menu">
            <nav className="container-site py-6 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `py-3 border-b border-white/5 font-display text-lg font-semibold ${isActive ? "text-brand" : "text-white"}`}
                  data-testid={`mobile-nav-link-${idFor(l.to)}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" onClick={() => setOpen(false)} className="btn-primary mt-4" data-testid="mobile-quote-button">
                {t.nav.quote}
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
